
import NotificationCheck from "@/assets/icons/NotificationCheck";
import NotificationRead from "@/assets/icons/NotificationRead";
import NotificationUnread from "@/assets/icons/NotificationUnread";
import FaceliftSelect from "@/components/Atoms/FaceliftSelect/FaceliftSelect";
import { SelectItem } from "@/components/Atoms/FaceliftSelect/FaceliftSelect.types";
import FaceliftText from "@/components/Atoms/FaceliftText";
import Show from "@/components/Molecules/Show";
import { useColorModeValue } from "@/components/ui/color-mode-utils";
import { colors } from "@/components/ui/theme";
import { Box } from "@chakra-ui/react";
import { SingleValue } from "chakra-react-select";
import { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router";
import { useInfiniteNotifications } from "@/hooks/service-hooks/notifications";
import { useNotificationContext } from "@/components/ui/notification-provider";
import dayjs from 'dayjs';
import NotificationsListSkeleton from "@/components/Templates/Notifications/NotificationsListSkeleton";
import { getDateRangeForNotifications } from "@/utils/dateUtils";
import { dateRanges } from "@/constants";

const NotificationsListView = () => {
  const statusFilters: SelectItem[] = [{ label: 'All', value: '' }, { label: 'Unread', value: 'unread' }];
  const [status, setStatus] = useState<SelectItem>({ label: 'All', value: '' });
  const [dateRange, setDateRange] = useState<SelectItem>({ label: 'This Year', value: 'this_year' });
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const navigate = useNavigate();
  const textColor = useColorModeValue('gray.800', 'gray.100');
  const { userId } = useNotificationContext();

  const handleItemClick = (notificationId: string) => {
    if (notificationId) {
      navigate(`/notifications/${notificationId}`);
    }
  };

  // Convert status filter to all boolean
  const all = useMemo(() => status.value === '' ? true : false, [status.value]);

  // Use infinite query for automatic pagination
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
  } = useInfiniteNotifications({
    userId: userId || null,
    limit: 11,
    all: all,
    date: getDateRangeForNotifications(dateRange.value as string),
    enabled: !!userId,
  });

  // Flatten all pages into single array
  const allNotifications = useMemo(
    () => data?.pages.flatMap(page => page.items) || [],
    [data]
  );

  // Scroll handler for infinite scroll
  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = scrollContainer;
      const isNearBottom = scrollHeight - scrollTop - clientHeight < 100;

      // Load more if near bottom, not fetching, and has more pages
      if (isNearBottom && !isFetchingNextPage && hasNextPage) {
        fetchNextPage();
      }
    };

    scrollContainer.addEventListener('scroll', handleScroll, { passive: true });
    return () => scrollContainer.removeEventListener('scroll', handleScroll);
  }, [isFetchingNextPage, hasNextPage, fetchNextPage]);

  return (
    <Box
      p={{ base: 4, md: 8 }}
      bg={{ base: '{colors.gray.50}', _dark: '{colors.gray.900}' }}
    >
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Box>
          <FaceliftText fontWeight={600} fontSize="x-large">
            All Notifications
          </FaceliftText>
        </Box>
        <Box display="flex" gap={2}>
          <Box width="100px">
            <FaceliftSelect
              items={statusFilters}
              isSearchable={false}
              value={status}
              onChange={(newValue: SingleValue<SelectItem>) => setStatus(newValue as SelectItem)}
            />
          </Box>
          <Box width="120px">
            <FaceliftSelect
              items={dateRanges}
              isSearchable={false}
              value={dateRange}
              onChange={(newValue: SingleValue<SelectItem>) => setDateRange(newValue as SelectItem)}
            />
          </Box>
        </Box>
      </Box>
      <Box
        ref={scrollContainerRef}
        overflowY="auto"
        height={750}
        bg={{ base: 'white', _dark: colors.tabelCellDark }}
        p={4}
        mt={4}
        borderRadius="md"
      >
        <Show when={!isLoading && allNotifications.length === 0}>
          <Box textAlign="center" py={8}>
            <FaceliftText fontSize="md" color="gray.500" fontWeight="bold">
              No notifications found
            </FaceliftText>
          </Box>
        </Show>
        <Show when={allNotifications.length > 0}>
          <Box display="flex" flexDirection="column" gap={2}>
            {allNotifications.map((item) => (
              <Box
                key={item.notificationId}
                p={1}
                pr={4}
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                cursor="pointer"
                bg={!item.isRead ? { base: 'gray.100', _dark: 'gray.950' } : { base: 'white', _dark: 'gray.800' }}
                color={textColor}
                _hover={{
                  bg: 'brand.solidGradient',
                  color: 'white',
                  cursor: 'pointer',
                }}
                onClick={() => handleItemClick(item.notificationId)}
                borderRadius="md"
                gap={2}
                borderLeft={!item.isRead ? "3px solid" : "1px solid"}
                shadow={{ _dark: !item.isRead ? 'xs' : 'sm', _light: 'sm' }}
              >
                <Box display="flex" alignItems="center">
                  <Box display="flex" alignItems="center">
                    <NotificationCheck height={30} width={35} />
                  </Box>
                  <Box display="flex" flexDirection="column">
                    <Box fontSize="sm" fontWeight={!item.isRead ? 600 : 500}>{item.title}</Box>
                    <Box fontSize="xs" fontWeight={!item.isRead ? 600 : 400}>{item.body}</Box>
                    {item.createdOn && (
                      <Box fontSize="2xs" fontWeight={!item.isRead ? 600 : 400} opacity={0.7}>
                        {dayjs(item.createdOn).tz('America/New_York').fromNow()}
                      </Box>
                    )}
                  </Box>
                </Box>
                <Box display="flex" alignItems="center">
                  <Show when={!item.isRead}>
                    <NotificationRead height={30} width={35} />
                  </Show>
                  <Show when={item.isRead}>
                    <NotificationUnread height={30} width={35} />
                  </Show>
                </Box>
              </Box>
            ))}
          </Box>
        </Show>
        <Show when={isLoading}>
          <NotificationsListSkeleton rows={10} />
        </Show>
        <Show when={isFetchingNextPage}>
          <NotificationsListSkeleton rows={3} />
        </Show>
      </Box >
    </Box >
  );
};

export default NotificationsListView;